import { useState } from "react";
import { Play, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { Spinner } from "@/components/ui/spinner";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { TableCard } from "@/components/ui/table-card";
import { PageHeader } from "@/components/layout/PageHeader";
import { OckStagedRow } from "@/components/scrapers/OckStagedRow";
import { StagedEventSheet } from "@/components/scrapers/StagedEventSheet";
import {
  useApproveStaged,
  useBulkApproveStaged,
  useBulkRejectStaged,
  useEnrichStaged,
  useOckStagedEvents,
  useRunScraper,
} from "@/hooks/useScrapers";
import type { StagedEvent } from "@/types";

export default function OckScrapersPage() {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [sheetEvent, setSheetEvent] = useState<StagedEvent | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState(50);
  const [cursorStack, setCursorStack] = useState<string[]>([]);

  const cursor = cursorStack.at(-1);
  const { data, isLoading, error } = useOckStagedEvents({
    status: statusFilter,
    search,
    cursor,
    limit,
  });
  const staged = data?.data ?? [];
  const hasMore = data?.hasMore ?? false;
  const page = cursorStack.length + 1;
  const handleLimitChange = (v: string) => {
    setLimit(Number(v));
    setCursorStack([]);
  };
  const goNext = () => {
    if (data?.nextCursor) setCursorStack((s) => [...s, data.nextCursor!]);
  };
  const goPrev = () => setCursorStack((s) => s.slice(0, -1));

  const runScraper = useRunScraper();
  const approveStaged = useApproveStaged();
  const enrich = useEnrichStaged();
  const bulkApprove = useBulkApproveStaged();
  const bulkReject = useBulkRejectStaged();

  const pendingStaged = staged.filter((e) => e.status === "pending");
  const allSelected = pendingStaged.length > 0 && selected.size === pendingStaged.length;
  const selectedPendingIds = [...selected].filter(
    (id) => staged.find((e) => e.id === id)?.status === "pending",
  );

  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(pendingStaged.map((e) => e.id)));
  };
  const toggleOne = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const handleRun = async () => {
    try {
      const result = await runScraper.mutateAsync();
      toast.success(
        `Pobrano ${result.scraped}, dodano ${result.inserted}, pominięto ${result.skipped}`,
      );
    } catch (err) {
      toast.error("Nie udało się uruchomić skrobaka", {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  const handleBulkEnrich = async () => {
    const ids = [...selected];
    try {
      await enrich.mutateAsync(ids);
      toast.success(`Wzbogacono ${ids.length} filmów`);
    } catch (err) {
      toast.error("Nie udało się wzbogacić filmów", {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  const handleBulkApprove = async () => {
    try {
      await bulkApprove.mutateAsync({
        ids: selectedPendingIds,
        destination: "movies",
      });
      setSelected(new Set());
      toast.success(`Zatwierdzono ${selectedPendingIds.length} filmów`);
    } catch (err) {
      toast.error("Nie udało się zatwierdzić filmów", {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  const handleBulkReject = async () => {
    try {
      await bulkReject.mutateAsync(selectedPendingIds);
      setSelected(new Set());
      toast.success(`Odrzucono ${selectedPendingIds.length} filmów`);
    } catch (err) {
      toast.error("Nie udało się odrzucić filmów", {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  const handleSheetApprove = async (values: {
    venueId?: string;
    categoryId?: string;
    destination: "events" | "movies";
    title: string;
    eventDescription: string;
    date: string;
    registrationType: string;
    registrationValue: string;
  }) => {
    if (!sheetEvent) return;
    try {
      await approveStaged.mutateAsync({ id: sheetEvent.id, ...values });
      setSheetOpen(false);
      toast.success("Wydarzenie zatwierdzone");
    } catch (err) {
      toast.error("Nie udało się zatwierdzić", {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  const handleSheetReject = async () => {
    setSheetOpen(false);
  };

  return (
    <div>
      <PageHeader
        title="OCK"
        description="Filmy kinowe z Ostrołęckiego Centrum Kultury"
        action={
          <Button onClick={handleRun} disabled={runScraper.isPending}>
            {runScraper.isPending ? (
              <Spinner className="h-4 w-4 mr-2" />
            ) : (
              <Play className="h-4 w-4 mr-2" />
            )}
            {runScraper.isPending ? "Pobieranie..." : "Uruchom skrobaka"}
          </Button>
        }
      />

      {selected.size > 0 && (
        <div className="flex items-center gap-3 mb-4 px-4 py-2.5 bg-white border border-black/5 rounded-xl shadow-sm">
          <span className="text-sm font-medium">Zaznaczono: {selected.size}</span>
          <Button
            size="sm"
            variant="outline"
            onClick={handleBulkEnrich}
            disabled={enrich.isPending}
          >
            {enrich.isPending && <Spinner className="h-3.5 w-3.5 mr-1.5" />}
            {enrich.isPending ? "Wzbogacanie..." : "Wzbogać zaznaczone"}
          </Button>
          {selectedPendingIds.length > 0 && (
            <>
              <Button
                size="sm"
                className="bg-green-600 hover:bg-green-700"
                onClick={handleBulkApprove}
                disabled={bulkApprove.isPending}
              >
                {bulkApprove.isPending && <Spinner className="h-3.5 w-3.5 mr-1.5" />}
                {bulkApprove.isPending
                  ? "Zatwierdzanie..."
                  : `Zatwierdź (${selectedPendingIds.length})`}
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={handleBulkReject}
                disabled={bulkReject.isPending}
              >
                {bulkReject.isPending && <Spinner className="h-3.5 w-3.5 mr-1.5" />}
                {bulkReject.isPending ? "Odrzucanie..." : `Odrzuć (${selectedPendingIds.length})`}
              </Button>
            </>
          )}
        </div>
      )}

      <div className="flex items-center gap-2 mb-4">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            placeholder="Szukaj..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCursorStack([]);
            }}
            className="pl-8"
          />
        </div>
        <Select
          value={statusFilter}
          onValueChange={(v) => {
            setStatusFilter(v);
            setCursorStack([]);
          }}
        >
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Wszystkie</SelectItem>
            <SelectItem value="pending">Oczekujące</SelectItem>
            <SelectItem value="approved">Zatwierdzone</SelectItem>
            <SelectItem value="rejected">Odrzucone</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-12 rounded-xl bg-white animate-pulse border border-black/5" />
          ))}
        </div>
      )}
      {error && <p className="text-destructive text-sm">Nie udało się załadować filmów.</p>}

      {!isLoading && !error && (
        <TableCard>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox checked={allSelected} onCheckedChange={toggleAll} />
                </TableHead>
                <TableHead className="w-[70px]"></TableHead>
                <TableHead>Tytuł</TableHead>
                <TableHead>Data</TableHead>
                <TableHead>Rok</TableHead>
                <TableHead>Czas</TableHead>
                <TableHead>Sala</TableHead>
                <TableHead>Język</TableHead>
                <TableHead>Oceny</TableHead>
                <TableHead>Status</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {staged.length === 0 ? (
                <tr>
                  <td colSpan={11} className="text-center py-12 text-muted-foreground text-sm">
                    Brak filmów.
                  </td>
                </tr>
              ) : (
                staged.map((event) => (
                  <OckStagedRow
                    key={event.id}
                    event={event}
                    selected={selected.has(event.id)}
                    onToggle={() => toggleOne(event.id)}
                    onEdit={() => {
                      setSheetEvent(event);
                      setSheetOpen(true);
                    }}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableCard>
      )}

      {!isLoading && (staged.length > 0 || cursorStack.length > 0) && (
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Strona {page}</span>
            <Select value={String(limit)} onValueChange={handleLimitChange}>
              <SelectTrigger className="w-24 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
            <span>na stronę</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={goPrev}
              disabled={cursorStack.length === 0}
            >
              <ChevronLeft className="h-4 w-4" />
              Poprzednia
            </Button>
            <Button variant="outline" size="sm" onClick={goNext} disabled={!hasMore}>
              Następna
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <StagedEventSheet
        event={sheetEvent}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        onApprove={handleSheetApprove}
        onReject={handleSheetReject}
        isPending={approveStaged.isPending}
      />
    </div>
  );
}
